"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import type { SearchBarComponent, SearchBarPopularTerm } from "../../types";
import { Search } from "lucide-react";
import { RuntimeLink, useLocale, useRuntimeNavigate } from "../../runtime/RendererRuntime";
import { t } from "../../i18n/strings";
import { resolveLinkHref } from "../../utils/links";

const SECTION_ID = "search-bar-section";

/**
 * Site-wide search bar. Two layout modes, both CMS-driven:
 *   - Single column (legacy): just the title + input, centered.
 *   - Two columns (when `popularSearches` is set): left search input,
 *     right "Les termes les plus recherchés" chip cloud. Mobile stacks.
 *
 * Color variants (`bold`): white-on-blue (default for landing) or blue-on-
 * cyan (lighter, for inner pages). Chip styling adapts to match.
 *
 * The input shows live keyword suggestions (one item per line) sourced
 * from `/api/search/keywords` — same backend as the header search. The
 * matched substring is bolded inline so the user can see why each
 * suggestion was offered. Clicking the magnifier icon (or pressing Enter)
 * always submits — to `/search?q=…` when typed, or to the bare `/search`
 * landing when empty.
 */
export function SearchBarRenderer({ component }: { component: SearchBarComponent }) {
	const { title, searchPlaceholder, bold, popularSearchesTitle, popularSearches } = component.content;
	const isBold = bold === true || String(bold) === "true";
	const hasPopular = !!popularSearchesTitle && Array.isArray(popularSearches) && popularSearches.length > 0;

	const sectionClass = isBold
		? "mx-[calc(50%-50vw)] apa-surface-dark py-10"
		: "mx-[calc(50%-50vw)] bg-[var(--color-surface-1)] py-10";
	const titleClass = isBold
		? "text-left text-lg xl:text-xl font-semibold text-white md:text-xl"
		: "text-left text-lg xl:text-xl font-semibold text-[#0A0A0A] md:text-xl";
	const iconColorClass = isBold ? "text-[#00B862]" : "text-[#00B862]";

	return (
		<section className={sectionClass} aria-label={title ?? undefined}>
			<div
				className={
					hasPopular
						? "mx-auto grid max-w-7xl gap-8 px-6 md:px-8 md:grid-cols-2 md:items-start"
						: "mx-auto flex max-w-7xl justify-center px-6 md:px-8"
				}
			>
				{/* LEFT — search field with suggestion dropdown */}
				<div className={hasPopular ? "w-full" : "w-full max-w-2xl"}>
					{title ? (
						<h4 className={titleClass} style={{ display: "block", width: "100%", marginBottom: 12 }}>
							{title}
						</h4>
					) : null}
					<SearchInputWithSuggestions
						inputId={`${SECTION_ID}-${component.key ?? "main"}`}
						placeholder={searchPlaceholder}
						iconColorClass={iconColorClass}
					/>
				</div>

				{/* RIGHT — popular searches (only when CMS provides them) */}
				{hasPopular && (
					<PopularSearchesColumn
						title={popularSearchesTitle!}
						terms={popularSearches!}
						isBold={isBold}
						titleClass={titleClass}
					/>
				)}
			</div>
		</section>
	);
}

/**
 * The actual input + dropdown. Forwarded `as` a self-contained block so the
 * left-column markup stays simple and the suggestion state is local to the
 * input. Submit goes to /search?q=…; empty submit goes to bare /search.
 */
function SearchInputWithSuggestions({
	inputId,
	placeholder,
	iconColorClass,
}: {
	inputId: string;
	placeholder: string;
	iconColorClass: string;
}) {
	const [query, setQuery] = useState("");
	const [focused, setFocused] = useState(false);
	const [suggestions, setSuggestions] = useState<string[]>([]);
	const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const abortRef = useRef<AbortController | null>(null);
	const cacheRef = useRef<Record<string, string[]>>({});
	const navigate = useRuntimeNavigate();
	const locale = useLocale();

	const submit = (value: string) => {
		const trimmed = value.trim();
		navigate(trimmed ? `/search?q=${encodeURIComponent(trimmed)}` : "/search");
	};

	const fetchSuggestions = async (q: string) => {
		abortRef.current?.abort();
		const controller = new AbortController();
		abortRef.current = controller;
		try {
			const langParam = locale && locale !== "fr" ? `lang=${encodeURIComponent(locale)}` : "";
			const url = q
				? `/api/search/keywords?q=${encodeURIComponent(q)}${langParam ? `&${langParam}` : ""}`
				: `/api/search/keywords${langParam ? `?${langParam}` : ""}`;
			const res = await fetch(url, { signal: controller.signal });
			if (!res.ok) return;
			const payload = await res.json();
			if (!Array.isArray(payload?.keywords)) return;
			const list = payload.keywords.filter((k: unknown): k is string => typeof k === "string");
			cacheRef.current[q] = list;
			if (query.trim() === q) setSuggestions(list);
		} catch (error) {
			if ((error as Error).name !== "AbortError") setSuggestions([]);
		}
	};

	useEffect(() => {
		if (!focused) {
			if (debounceRef.current) { clearTimeout(debounceRef.current); debounceRef.current = null; }
			abortRef.current?.abort();
			return;
		}
		const trimmed = query.trim();
		const cached = cacheRef.current[trimmed];
		if (cached) {
			setSuggestions(cached);
			return;
		}
		if (debounceRef.current) clearTimeout(debounceRef.current);
		debounceRef.current = setTimeout(() => void fetchSuggestions(trimmed), trimmed ? 300 : 0);
		return () => {
			if (debounceRef.current) { clearTimeout(debounceRef.current); debounceRef.current = null; }
		};
	}, [focused, query]);

	const visible = useMemo(() => suggestions.slice(0, 6), [suggestions]);

	return (
		<form
			onSubmit={(e) => { e.preventDefault(); submit(query); }}
			className="relative block w-full"
		>
			<label className="sr-only" htmlFor={inputId}>{placeholder}</label>
			<input
				id={inputId}
				type="search"
				className="h-12 w-full rounded-sm border-none bg-white px-4 pr-14 text-base xl:text-lg text-[#00B862] outline-none ring-0 placeholder-gray-500"
				placeholder={placeholder}
				autoComplete="off"
				value={query}
				onChange={(e) => setQuery(e.target.value)}
				onFocus={() => setFocused(true)}
				onBlur={() => setTimeout(() => setFocused(false), 120)}
			/>
			<button
				type="submit"
				aria-label={t(locale, "search")}
				className={`absolute right-2 top-1/2 -translate-y-1/2 inline-flex h-9 w-9 items-center justify-center rounded-full ${iconColorClass} hover:bg-[#00B862]/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[#00B862]`}
			>
				<Search className="h-5 w-5" />
			</button>
			{focused && visible.length > 0 && (
				<SuggestionPanel query={query.trim()} suggestions={visible} onSelect={submit} />
			)}
		</form>
	);
}

/** Right-column "Les termes les plus recherchés" chip cloud. Pulled into a
 *  helper so the main render path stays linear and easy to read. */
function PopularSearchesColumn({
	title,
	terms,
	isBold,
	titleClass,
}: {
	title: string;
	terms: SearchBarPopularTerm[];
	isBold: boolean;
	titleClass: string;
}) {
	const chipClass = isBold
		? "inline-flex items-center rounded-md border border-white/70 bg-transparent px-3 py-1.5 text-sm xl:text-base font-medium text-white transition hover:bg-white/10"
		: "inline-flex items-center rounded-md border border-[#00B862]/40 bg-white/30 px-3 py-1.5 text-sm xl:text-base font-medium text-[#00B862] transition hover:bg-white/60";

	return (
		<div className="w-full">
			<h4 className={titleClass} style={{ display: "block", width: "100%", marginBottom: 12 }}>
				{title}
			</h4>
			<div className="flex flex-wrap gap-2">
				{terms.map((term, index) => {
					const href = resolveLinkHref(term.href) || `/search?q=${encodeURIComponent(term.label)}`;
					return (
						<RuntimeLink key={`${term.label}-${index}`} href={href} className={chipClass}>
							{term.label}
						</RuntimeLink>
					);
				})}
			</div>
		</div>
	);
}

/**
 * Vertical suggestion list dropdown — one row per item, matched
 * substring bolded inline. Mirrors the header search panel so users
 * see consistent behaviour across both entry points.
 */
function SuggestionPanel({
	query,
	suggestions,
	onSelect,
}: {
	query: string;
	suggestions: string[];
	onSelect: (value: string) => void;
}) {
	const normalisedQuery = useMemo(() => normaliseForMatch(query), [query]);
	return (
		<div
			role="listbox"
			className="absolute left-0 right-0 top-full z-40 mt-2 overflow-hidden rounded-md border border-[#00B862]/15 bg-white text-[#121212] shadow-lg"
		>
			<div className="px-4 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wide text-[#121212]/55">
				{query ? "Suggestions" : "Recherches populaires"}
			</div>
			<ul className="py-1">
				{suggestions.map((keyword) => (
					<li key={keyword}>
						<button
							type="button"
							role="option"
							aria-selected={false}
							className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm text-[#121212] transition hover:bg-[#00B862]/8 focus:bg-[#00B862]/10 focus:outline-none"
							onMouseDown={(e) => e.preventDefault()}
							onClick={() => onSelect(keyword)}
						>
							<Search size={14} className="shrink-0 text-[#00B862]/70" />
							<span className="truncate">{renderHighlighted(keyword, normalisedQuery)}</span>
						</button>
					</li>
				))}
			</ul>
		</div>
	);
}

/** Strips diacritics + lowercases — same rule as the server scoring. */
function normaliseForMatch(value: string): string {
	return value.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase();
}

/**
 * Wraps the substring of `label` that matches the user's normalised
 * query in `<strong>`. Walks the original label so diacritics render
 * untouched, comparing positions against the normalised label.
 */
function renderHighlighted(label: string, normalisedQuery: string) {
	if (!normalisedQuery) return label;
	const normLabel = normaliseForMatch(label);
	const idx = normLabel.indexOf(normalisedQuery);
	if (idx < 0) return label;
	const end = idx + normalisedQuery.length;
	return (
		<>
			{label.slice(0, idx)}
			<strong className="font-semibold text-[#00B862]">{label.slice(idx, end)}</strong>
			{label.slice(end)}
		</>
	);
}
